import { StrictMode, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Calculator,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Code2,
  ClipboardList,
  ImagePlus,
  Link,
  List,
  ListOrdered,
  LogOut,
  Maximize2,
  MessageSquare,
  PackageCheck,
  Plus,
  Search,
  SquarePen,
  Table2,
  Truck,
  UserRound,
  X,
} from "lucide-react";
import { adminModules, type AdminModule, type AdminRow, type ModuleConfig } from "./data";
import {
  addMemberMemo,
  addQuoteMemo,
  answerInquiry,
  createQuoteOffer,
  deleteQuoteOffer,
  deleteQuote,
  deleteQuoteMemo,
  deleteNotice,
  deleteProduct,
  fetchCategories,
  fetchModuleDetail,
  fetchModuleRows,
  resetMemberPassword,
  saveNotice,
  saveProduct,
  updateMember,
  updateOrder,
  updateQuote,
  uploadFile,
  type DetailRecord,
  type Pagination as ApiPagination,
} from "./api";
import { getApiToken, requestApi, setApiToken } from "../shared/api";
import "../styles.css";

type DialogState = {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm?: () => void;
} | null;

const textValue = (record: DetailRecord | null | undefined, ...keys: string[]) => {
  for (const key of keys) {
    const value = record?.[key];
    if (value !== undefined && value !== null && value !== "") return String(value);
  }
  return "";
};

const dateValue = (value: unknown) => value
  ? new Date(String(value)).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" })
  : "-";

const moneyValue = (value: unknown) => `${Number(value || 0).toLocaleString("ko-KR")}원`;
const todayInKorea = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Seoul" }).format(new Date());

const detailStatusLabel: Record<string, string> = {
  active: "정상",
  suspended: "정지",
  withdrawn: "탈퇴",
  on_sale: "판매중",
  stopped: "판매중지",
  sold_out: "품절",
  ordered: "주문완료",
  confirmed: "주문확인",
  shipping: "배송중",
  delivered: "배송완료",
  completed: "확정",
  cancelled: "취소",
  requested: "견적요청",
  consulting: "상담중",
  quoted: "견적등록",
  paid: "결제완료",
  waiting: "답변대기",
  answered: "답변완료",
  inactive: "미사용",
};
const paymentStatusLabel: Record<string, string> = {
  ready: "결제대기",
  pending: "승인중",
  done: "결제완료",
  failed: "결제실패",
  cancelled: "결제취소",
  refunded: "환불완료",
};

function rowFromDetail(module: AdminModule, id: string, detail: DetailRecord): AdminRow {
  const status = textValue(detail, "status", "saleStatus", "sale_status");
  const base = { id, no: "", status: detailStatusLabel[status] || status };
  if (module === "members") return {
    ...base,
    code: textValue(detail, "managementCode", "management_code"),
    loginId: textValue(detail, "loginId", "login_id"),
    name: textValue(detail, "name"),
    email: textValue(detail, "email"),
    phone: textValue(detail, "phone"),
  };
  if (module === "products") return {
    ...base,
    code: textValue(detail, "managementCode", "management_code"),
    name: textValue(detail, "name"),
    price: textValue(detail, "regularPrice", "regular_price"),
    salePrice: textValue(detail, "salePrice", "sale_price"),
  };
  if (module === "orders") return {
    ...base,
    orderNo: textValue(detail, "orderNumber", "order_number"),
    product: textValue(detail, "productName", "product_name", "product_name_snapshot"),
    member: textValue(detail, "userName", "user_name"),
    phone: textValue(detail, "recipientPhone", "recipient_phone", "user_phone"),
  };
  if (module === "quotes") return {
    ...base,
    quoteNo: textValue(detail, "managementCode", "management_code"),
    product: textValue(detail, "productName", "product_name"),
    member: textValue(detail, "userName", "user_name"),
    phone: textValue(detail, "phone", "user_phone"),
    due: textValue(detail, "desiredDeliveryDate", "desired_delivery_date"),
  };
  if (module === "notices") return {
    ...base,
    noticeNo: textValue(detail, "managementCode", "management_code"),
    title: textValue(detail, "title"),
  };
  return {
    ...base,
    inquiryNo: textValue(detail, "managementCode", "management_code"),
    title: textValue(detail, "title"),
    type: textValue(detail, "inquiryType", "inquiry_type"),
    member: textValue(detail, "userName", "user_name"),
    phone: textValue(detail, "phone"),
  };
}

const menuIcons = {
  members: UserRound,
  products: SquarePen,
  orders: CircleDollarSign,
  quotes: Calculator,
  notices: ClipboardList,
  inquiries: MessageSquare,
};

function moduleFromUrl(): AdminModule {
  const value = new URL(window.location.href).searchParams.get("menu") as AdminModule | null;
  return adminModules.some((module) => module.id === value) ? value! : "members";
}

function updateUrl(menu: AdminModule, detail?: string) {
  const url = new URL(window.location.href);
  url.searchParams.set("menu", menu);
  if (detail) url.searchParams.set("detail", detail);
  else url.searchParams.delete("detail");
  window.history.pushState({ menu, detail }, "", url);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function AppDialog({ dialog, onClose }: { dialog: DialogState; onClose: () => void }) {
  if (!dialog) return null;
  const confirm = () => {
    const action = dialog.onConfirm;
    onClose();
    action?.();
  };
  return (
    <div className="bo-dialog-layer" role="presentation" onMouseDown={onClose}>
      <section className="bo-dialog" role="dialog" aria-modal="true" aria-labelledby="dialog-title" onMouseDown={(event) => event.stopPropagation()}>
        <h2 id="dialog-title">{dialog.title}</h2>
        <p>{dialog.message}</p>
        <div className="bo-dialog-actions">
          {dialog.cancelLabel ? <button className="bo-button outline" type="button" onClick={onClose}>{dialog.cancelLabel}</button> : null}
          <button className="bo-button primary" type="button" onClick={confirm}>{dialog.confirmLabel || "확인"}</button>
        </div>
      </section>
    </div>
  );
}

function Login({ onLogin }: { onLogin: () => void }) {
  const [id, setId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!id.trim() || !password.trim()) {
      setError("아이디와 비밀번호를 입력해 주세요.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const result = await requestApi<{ token: string }>("/admin/auth/login", { method: "POST", body: JSON.stringify({ loginId: id.trim(), password }) });
      setApiToken(result.token);
      onLogin();
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : "로그인에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <main className="bo-login-page">
      <form className="bo-login-form" onSubmit={submit}>
        <div className="bo-login-brand" aria-label="소담원 관리자">
          <img src="/assets/sodamwon-symbol-official.png" alt="" />
          <strong>SODAMWON</strong>
        </div>
        <h1>관리자 로그인</h1>
        <input aria-label="아이디" value={id} onChange={(event) => setId(event.target.value)} placeholder="아이디를 입력해 주세요." autoComplete="username" />
        <input aria-label="비밀번호" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="비밀번호를 입력해 주세요." type="password" autoComplete="current-password" />
        {error ? <p className="bo-login-error">{error}</p> : null}
        <button type="submit" disabled={submitting}>{submitting ? "로그인 중" : "로그인"}</button>
      </form>
    </main>
  );
}

function Sidebar({ active, onSelect, onLogout }: { active: AdminModule; onSelect: (id: AdminModule) => void; onLogout: () => void }) {
  return (
    <aside className="bo-sidebar">
      <strong className="bo-brand"><img src="/assets/sodamwon-symbol-official.png" alt="" /><span>SODAMWON</span></strong>
      <nav aria-label="관리자 메뉴">
        {adminModules.map((module) => {
          const Icon = menuIcons[module.id];
          return (
            <button className={active === module.id ? "active" : ""} type="button" key={module.id} onClick={() => onSelect(module.id)}>
              <Icon size={22} strokeWidth={1.6} aria-hidden="true" />
              <span>{module.title.replace(" 정보", "").replace("결제/배송", "결제/배송")}</span>
            </button>
          );
        })}
      </nav>
      <button className="bo-logout" type="button" onClick={onLogout}><LogOut size={21} aria-hidden="true" />로그아웃</button>
    </aside>
  );
}

function SearchPanel({ config, onSearch }: { config: ModuleConfig; onSearch: (values: Record<string, string>) => void }) {
  const [values, setValues] = useState<Record<string, string>>({});
  return (
    <section className="bo-block bo-search-block">
      <h2>{config.searchTitle}</h2>
      <div className={`bo-search-grid fields-${config.searchFields.length}`}>
        {config.searchFields.map((field) => (
          <label key={field.key} className={field.key === "status" && config.searchFields.length > 4 ? "wide" : ""}>
            <span>{field.label}</span>
            {field.type === "select" ? (
              <select value={values[field.key] || ""} onChange={(event) => setValues((current) => ({ ...current, [field.key]: event.target.value }))}>
                {(field.options || []).map((option) => <option key={option}>{option}</option>)}
              </select>
            ) : (
              <span className="bo-search-input"><Search size={16} aria-hidden="true" /><input value={values[field.key] || ""} onChange={(event) => setValues((current) => ({ ...current, [field.key]: event.target.value }))} placeholder={field.placeholder} /></span>
            )}
          </label>
        ))}
      </div>
      <button className="bo-button primary bo-query-button" type="button" onClick={() => onSearch(values)}>조회</button>
    </section>
  );
}

function Pagination({ value = { page: 1, pageSize: 10, total: 0, totalPages: 1 }, onChange = () => undefined }: { value?: ApiPagination; onChange?: (page: number) => void }) {
  const pages = Array.from({ length: value.totalPages }, (_, index) => index + 1).slice(Math.max(0, value.page - 3), Math.max(5, value.page + 2));
  return (
    <nav className="bo-pagination" aria-label="페이지 이동">
      <button type="button" aria-label="이전 페이지" disabled={value.page <= 1} onClick={() => onChange(value.page - 1)}><ChevronLeft size={19} /></button>
      {pages.map((page) => <button className={page === value.page ? "active" : ""} type="button" key={page} onClick={() => onChange(page)}>{page}</button>)}
      <button type="button" aria-label="다음 페이지" disabled={value.page >= value.totalPages} onClick={() => onChange(value.page + 1)}><ChevronRight size={19} /></button>
    </nav>
  );
}

function DataTable({ config, rows, selectedId, selectedIds, selectable, onSelect, onOpen, onToggle }: { config: ModuleConfig; rows: AdminRow[]; selectedId: string; selectedIds: string[]; selectable: boolean; onSelect: (id: string) => void; onOpen: (id: string) => void; onToggle: (id: string) => void }) {
  const allSelected = selectable && rows.length > 0 && rows.every((row) => selectedIds.includes(row.id));
  return (
    <div className="bo-table-wrap">
      <table className="bo-data-table">
        <thead><tr>{selectable ? <th className="bo-check-cell"><input type="checkbox" aria-label="전체 선택" checked={allSelected} onChange={() => rows.forEach((row) => { if (allSelected === selectedIds.includes(row.id)) onToggle(row.id); })} /></th> : null}{config.columns.map((column) => <th className={column.align ? `is-${column.align}` : ""} key={column.key}>{column.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr className={selectedId === row.id ? "selected" : ""} key={row.id} tabIndex={0} onClick={() => onSelect(row.id)} onDoubleClick={() => onOpen(row.id)} onKeyDown={(event) => { if (event.key === "Enter") onOpen(row.id); }}>
              {selectable ? <td className="bo-check-cell"><input type="checkbox" aria-label={`${row.no} 선택`} checked={selectedIds.includes(row.id)} onClick={(event) => event.stopPropagation()} onChange={() => onToggle(row.id)} /></td> : null}
              {config.columns.map((column) => <td className={`${column.align ? `is-${column.align}` : ""} ${column.key === "status" && row[column.key].includes("정지") ? "is-danger" : ""}`} key={column.key} title={row[column.key]}><span>{row[column.key]}</span></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MemberDetail({ row, detail, onRefresh, onSaved, onPasswordReset, onOpenOrder }: { row: AdminRow; detail: DetailRecord; onRefresh: () => Promise<void>; onSaved: (preserveScroll?: boolean) => void; onPasswordReset: () => void; onOpenOrder: (id: string) => void }) {
  const [memo, setMemo] = useState("");
  const [name, setName] = useState(textValue(detail, "name"));
  const [phone, setPhone] = useState(textValue(detail, "phone"));
  const [email, setEmail] = useState(textValue(detail, "email"));
  const [status, setStatus] = useState(textValue(detail, "status") || "active");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setName(textValue(detail, "name"));
    setPhone(textValue(detail, "phone"));
    setEmail(textValue(detail, "email"));
    setStatus(textValue(detail, "status") || "active");
  }, [detail]);
  const memos = Array.isArray(detail.memos) ? detail.memos as DetailRecord[] : [];
  const recentOrders = Array.isArray(detail.recentOrders) ? detail.recentOrders as DetailRecord[] : [];
  const saveMember = async () => {
    setSaving(true);
    try {
      await updateMember(row.id, { name, phone, email, status });
      await onRefresh();
      onSaved(true);
    } finally { setSaving(false); }
  };
  const saveMemo = async () => {
    if (!memo.trim()) return;
    setSaving(true);
    try {
      await addMemberMemo(row.id, memo.trim());
      setMemo("");
      await onRefresh();
      onSaved(true);
    } finally { setSaving(false); }
  };
  return (
    <section className="bo-detail-stack">
      <div className="bo-section-title"><h2>회원 상세정보</h2><button className="bo-button primary small" type="button" disabled={saving} onClick={() => void saveMember()}>{saving ? "저장 중" : "저장"}</button></div>
      <div className="bo-field-table member-fields">
        <span className="label">회원 코드</span><span>{textValue(detail, "managementCode") || row.code}</span><span className="label">회원ID</span><span>{textValue(detail, "loginId") || row.loginId}</span><span className="label">회원명</span><span className="bo-field-control"><input value={name} onChange={(event) => setName(event.target.value)} /></span>
        <span className="label">가입일</span><span>{dateValue(detail.createdAt)}</span><span className="label required">휴대폰 번호</span><span className="bo-field-control"><input value={phone} onChange={(event) => setPhone(event.target.value)} /></span><span className="label required">이메일</span><span className="bo-field-control"><input value={email} onChange={(event) => setEmail(event.target.value)} /></span>
        <span className="label required">상태</span><span className="bo-field-control"><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="active">정상</option><option value="suspended">정지</option><option value="withdrawn">탈퇴</option></select></span><span className="label">성별</span><span>{textValue(detail, "gender") === "male" ? "남성" : textValue(detail, "gender") === "female" ? "여성" : "미입력"}</span><span className="label">생년월일</span><span>{textValue(detail, "birthDate") || "-"}</span>
        <span className="label">비밀번호</span><span className="span-five"><button className="bo-button outline tiny" type="button" onClick={onPasswordReset}>초기화</button><small>*난수 6자리로 초기화</small></span>
        <span className="label">메모</span><span className="span-five bo-memo-entry"><input value={memo} onChange={(event) => setMemo(event.target.value)} placeholder="메모를 입력해 주세요." /><button className="bo-button primary tiny" type="button" disabled={saving} onClick={() => void saveMemo()}>메모 등록</button></span>
        <div className="bo-memo-list span-all"><p>*등록된 메모는 최신순으로 노출됩니다.</p>{memos.length ? memos.map((item) => <div key={String(item.id)}><span>- [{dateValue(item.createdAt)}] {String(item.body || "")}</span></div>) : <div><span>- 등록된 메모가 없습니다.</span></div>}</div>
      </div>
      <h2>회원 결제 이력</h2>
      <div className="bo-table-wrap"><table className="bo-data-table"><thead><tr><th>No</th><th>주문 번호</th><th>상품명</th><th>상태</th><th>결제 금액</th><th>주문 일시</th></tr></thead><tbody>{recentOrders.length ? recentOrders.map((order, index) => <tr key={String(order.id)} onDoubleClick={() => onOpenOrder(String(order.id))}><td>{String(index + 1).padStart(2, "0")}</td><td className="link-cell">{String(order.orderNumber || "")}</td><td>{String(order.productName || "")}</td><td>{detailStatusLabel[String(order.status)] || String(order.status || "-")}</td><td>{moneyValue(order.totalAmount)}</td><td>{dateValue(order.orderedAt)}</td></tr>) : <tr><td className="bo-empty-cell" colSpan={6}>결제 이력이 없습니다.</td></tr>}</tbody></table></div>
    </section>
  );
}

function ProductDetail({ row, detail, onBack, onSaved, onDeleted }: { row?: AdminRow; detail?: DetailRecord; onBack: () => void; onSaved: () => void; onDeleted: () => void }) {
  const [tab, setTab] = useState<"basic" | "image" | "detail">("basic");
  const [categories, setCategories] = useState<Array<{ id: string; slug: string; name: string }>>([]);
  const [categoryId, setCategoryId] = useState(textValue(detail, "categoryId"));
  const [name, setName] = useState(textValue(detail, "name") || row?.name || "");
  const [saleStatus, setSaleStatus] = useState(textValue(detail, "saleStatus") || "on_sale");
  const [price, setPrice] = useState(String(detail?.regularPrice || (row?.price || "").replaceAll(",", "")));
  const initialPrice = Number(price || 0);
  const initialSalePrice = Number(detail?.salePrice || (row?.salePrice || row?.price || "0").replaceAll(",", ""));
  const [discountEnabled, setDiscountEnabled] = useState(initialSalePrice < initialPrice);
  const [discountType, setDiscountType] = useState<"원" | "%">("원");
  const [discountValue, setDiscountValue] = useState(String(Math.max(0, initialPrice - initialSalePrice) || ""));
  const [stockQuantity, setStockQuantity] = useState(textValue(detail, "stockQuantity") || "0");
  const [detailHtml, setDetailHtml] = useState(textValue(detail, "detailHtml"));
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const existingImages = Array.isArray(detail?.images) ? detail.images as DetailRecord[] : [];
  const imagePreview = (index: number) => imageFiles[index] ? URL.createObjectURL(imageFiles[index]) : String(existingImages[index]?.url || "");

  useEffect(() => { fetchCategories().then(setCategories).catch(() => setCategories([])); }, []);
  useEffect(() => {
    if (!detail) return;
    const regular = Number(detail.regularPrice || 0);
    const sale = Number(detail.salePrice || regular);
    setCategoryId(textValue(detail, "categoryId"));
    setName(textValue(detail, "name"));
    setSaleStatus(textValue(detail, "saleStatus") || "on_sale");
    setPrice(String(regular));
    setDiscountEnabled(sale < regular);
    setDiscountType("원");
    setDiscountValue(String(Math.max(0, regular - sale)));
    setStockQuantity(String(detail.stockQuantity || 0));
    setDetailHtml(textValue(detail, "detailHtml"));
  }, [detail]);

  const salePrice = useMemo(() => {
    const regular = Number(price) || 0;
    if (!discountEnabled) return regular;
    const discount = Number(discountValue) || 0;
    return Math.max(0, Math.round(discountType === "%" ? regular * (1 - discount / 100) : regular - discount));
  }, [discountEnabled, discountType, discountValue, price]);

  const submit = async () => {
    if (!categoryId || !name.trim()) return;
    setSaving(true);
    try {
      const imageFileIds = (await Promise.all([0, 1, 2].map(async (index) => imageFiles[index]
        ? (await uploadFile(imageFiles[index], "product", row?.id)).id
        : String(existingImages[index]?.fileId || "")))).filter(Boolean);
      await saveProduct({ categoryId, name: name.trim(), saleStatus, regularPrice: Number(price) || 0, salePrice, stockQuantity: Number(stockQuantity) || 0, detailHtml, imageFileIds }, row?.id);
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <section className="bo-detail-page">
      <div className="bo-tabs"><button className={tab === "basic" ? "active" : ""} onClick={() => setTab("basic")}>기본 정보</button><button className={tab === "image" ? "active" : ""} onClick={() => setTab("image")}>상품 이미지</button><button className={tab === "detail" ? "active" : ""} onClick={() => setTab("detail")}>상품 상세 정보</button></div>
      {tab === "basic" ? <>
        <DetailSection title="상품 분류 정보"><FieldRow label="상품 코드" value={textValue(detail, "managementCode") || row?.code || "저장 시 자동 생성"} /><FieldRow label="카테고리" required><select value={categoryId} onChange={(event) => setCategoryId(event.target.value)}><option value="">선택</option>{categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></FieldRow></DetailSection>
        <DetailSection title="상품 기본 정보"><FieldRow label="판매 여부" required><label><input type="radio" name="sale" checked={saleStatus === "on_sale"} onChange={() => setSaleStatus("on_sale")} /> 판매중</label><label><input type="radio" name="sale" checked={saleStatus === "stopped"} onChange={() => setSaleStatus("stopped")} /> 판매 중지</label></FieldRow><FieldRow label="상품명" required><input value={name} onChange={(event) => setName(event.target.value)} placeholder="제품명을 입력해 주세요." /></FieldRow></DetailSection>
        <DetailSection title="상품 가격 정보"><FieldRow label="정가" required><input type="number" min="0" value={price} onChange={(event) => setPrice(event.target.value)} placeholder="가격을 입력해 주세요." /></FieldRow><FieldRow label="할인 여부" required><label><input type="radio" name="discount" checked={discountEnabled} onChange={() => setDiscountEnabled(true)} /> 설정</label><label><input type="radio" name="discount" checked={!discountEnabled} onChange={() => setDiscountEnabled(false)} /> 설정 안 함</label><select value={discountType} disabled={!discountEnabled} onChange={(event) => setDiscountType(event.target.value as "원" | "%")}><option>원</option><option>%</option></select><input type="number" min="0" max={discountType === "%" ? 100 : undefined} value={discountValue} disabled={!discountEnabled} onChange={(event) => setDiscountValue(event.target.value)} placeholder="할인 값을 입력해 주세요." /></FieldRow><FieldRow label="판매가" value={`${salePrice.toLocaleString()}원`} /></DetailSection>
        <DetailSection title="상품 재고 정보"><FieldRow label="재고 수량"><input type="number" min="0" value={stockQuantity} onChange={(event) => setStockQuantity(event.target.value)} /></FieldRow></DetailSection>
      </> : null}
      {tab === "image" ? <DetailSection title="상품 이미지"><div className="bo-upload-note"><strong>이미지 등록</strong><span>*등록 가능 확장자 jpg/jpeg/png, 파일 최대 용량 3mb, 이미지 크기는 360px * 360px 크기를 권장드립니다.</span></div><div className="bo-image-registration"><div><span>대표 이미지</span><label><input type="file" accept=".jpg,.jpeg,.png" onChange={(event) => setImageFiles((current) => { const next = [...current]; if (event.target.files?.[0]) next[0] = event.target.files[0]; return next; })} /><span>{imagePreview(0) ? <img src={imagePreview(0)} alt="대표 이미지 미리보기" /> : <><Plus size={24} />업로드</>}</span></label></div><div><span>서브 이미지 <small>(최대 2장)</small></span><div className="bo-sub-images">{[1, 2].map((index) => <label key={index}><input type="file" accept=".jpg,.jpeg,.png" onChange={(event) => setImageFiles((current) => { const next = [...current]; if (event.target.files?.[0]) next[index] = event.target.files[0]; return next; })} /><span>{imagePreview(index) ? <img src={imagePreview(index)} alt={`서브 이미지 ${index} 미리보기`} /> : <><Plus size={24} />업로드</>}</span></label>)}</div></div></div></DetailSection> : null}
      {tab === "detail" ? <section className="bo-form-section"><h2>상품 상세 정보 <small>(&lt;-880px)</small></h2><div className="bo-detail-editor"><span>상세 내용</span><HtmlEditor value={detailHtml} onChange={setDetailHtml} /></div></section> : null}
      <div className="bo-form-actions"><button className="bo-button outline" type="button" onClick={onBack}>목록</button>{row ? <button className="bo-button outline" type="button" onClick={onDeleted}>삭제</button> : null}<button className="bo-button primary" disabled={saving || !categoryId || !name.trim()} type="button" onClick={() => void submit()}>{saving ? "저장 중" : "저장"}</button></div>
    </section>
  );
}

function HtmlEditor({ value = "", onChange = () => undefined }: { value?: string; onChange?: (value: string) => void }) {
  const editorRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [sourceMode, setSourceMode] = useState(false);
  const [source, setSource] = useState(value);
  useEffect(() => {
    setSource(value);
    if (editorRef.current && editorRef.current.innerHTML !== value) editorRef.current.innerHTML = value;
  }, [value]);
  const runCommand = (command: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
  };
  const insertImage = async (file?: File) => {
    if (!file || !file.type.startsWith("image/")) return;
    const uploaded = await uploadFile(file, "product-detail");
    if (!editorRef.current) return;
    const image = document.createElement("img");
    image.src = uploaded.url;
    image.alt = file.name;
    editorRef.current.appendChild(image);
    onChange(editorRef.current.innerHTML);
  };
  const toggleSource = () => {
    if (!sourceMode) setSource(editorRef.current?.innerHTML || "");
    else if (editorRef.current) { editorRef.current.innerHTML = source; onChange(source); }
    setSourceMode((current) => !current);
  };
  const insertLink = () => {
    const href = window.prompt("링크 주소를 입력해 주세요.", "https://");
    if (href) runCommand("createLink", href);
  };
  return <div className={`bo-html-editor ${fullscreen ? "is-fullscreen" : ""}`}>
    <div className="bo-editor-toolbar">
      <button type="button" onClick={() => runCommand("bold")} aria-label="굵게" title="굵게"><b>B</b></button>
      <button type="button" onClick={() => runCommand("italic")} aria-label="기울임" title="기울임"><i>I</i></button>
      <button type="button" onClick={() => runCommand("underline")} aria-label="밑줄" title="밑줄"><u>U</u></button>
      <select aria-label="글꼴" defaultValue="Pretendard" onChange={(event) => runCommand("fontName", event.target.value)}><option>Pretendard</option><option>NanumGothic</option><option>Arial</option></select>
      <select aria-label="글자 크기" defaultValue="14" onChange={(event) => runCommand("fontSize", event.target.value === "14" ? "2" : event.target.value === "16" ? "3" : "4")}><option>14</option><option>16</option><option>18</option></select>
      <label className="bo-editor-color" title="글자색"><input type="color" defaultValue="#31363f" aria-label="글자색" onChange={(event) => runCommand("foreColor", event.target.value)} /></label>
      <span className="bo-editor-divider" aria-hidden="true" />
      <button type="button" onClick={() => runCommand("justifyLeft")} aria-label="왼쪽 정렬" title="왼쪽 정렬"><AlignLeft size={14} /></button>
      <button type="button" onClick={() => runCommand("justifyCenter")} aria-label="가운데 정렬" title="가운데 정렬"><AlignCenter size={14} /></button>
      <button type="button" onClick={() => runCommand("justifyRight")} aria-label="오른쪽 정렬" title="오른쪽 정렬"><AlignRight size={14} /></button>
      <button type="button" onClick={() => runCommand("insertUnorderedList")} aria-label="글머리 목록" title="글머리 목록"><List size={14} /></button>
      <button type="button" onClick={() => runCommand("insertOrderedList")} aria-label="번호 목록" title="번호 목록"><ListOrdered size={14} /></button>
      <button type="button" onClick={insertLink} aria-label="링크 삽입" title="링크 삽입"><Link size={14} /></button>
      <button type="button" onClick={() => runCommand("insertHTML", '<table><tbody><tr><td>내용</td><td>내용</td></tr><tr><td>내용</td><td>내용</td></tr></tbody></table>')} aria-label="표 삽입" title="표 삽입"><Table2 size={14} /></button>
      <button type="button" onClick={() => imageInputRef.current?.click()} aria-label="이미지 삽입" title="이미지 삽입"><ImagePlus size={15} /></button>
      <span className="bo-editor-divider" aria-hidden="true" />
      <button type="button" onClick={() => runCommand("undo")} aria-label="실행 취소" title="실행 취소">↶</button>
      <button type="button" onClick={() => runCommand("redo")} aria-label="다시 실행" title="다시 실행">↷</button>
      <button type="button" onClick={toggleSource} aria-label="HTML 소스 보기" title="HTML 소스 보기"><Code2 size={14} /></button>
      <button type="button" onClick={() => setFullscreen((current) => !current)} aria-label="전체 화면" title="전체 화면"><Maximize2 size={14} /></button>
      <input ref={imageInputRef} className="bo-editor-image-input" type="file" accept="image/*" onChange={(event) => { void insertImage(event.target.files?.[0]); event.currentTarget.value = ""; }} />
    </div>
    {sourceMode ? <textarea className="bo-html-source" value={source} onChange={(event) => { setSource(event.target.value); onChange(event.target.value); }} aria-label="HTML 소스" /> : <div ref={editorRef} className="bo-html-editor-area" contentEditable suppressContentEditableWarning aria-label="상품 상세 내용" data-placeholder="상품 상세 내용을 입력해 주세요." onBlur={() => onChange(editorRef.current?.innerHTML || "")} />}
  </div>;
}

function DetailSection({ title, children }: React.PropsWithChildren<{ title: string }>) {
  return <section className="bo-form-section"><h2>{title}</h2><div className="bo-form-table">{children}</div></section>;
}

function FieldRow({ label, value, required, children }: React.PropsWithChildren<{ label: string; value?: string; required?: boolean }>) {
  return <div className="bo-form-row"><span className={required ? "required" : ""}>{label}</span><div>{children || value}</div></div>;
}

function InfoGrid({ entries, columns = 3 }: { entries: Array<{ label: string; value: React.ReactNode; span?: 2 | 3 }>; columns?: 2 | 3 }) {
  return <div className={`bo-info-grid columns-${columns}`}>{entries.map((entry, index) => <div className={entry.span ? `span-${entry.span}` : ""} key={`${entry.label}-${index}`}><span>{entry.label}</span><div>{entry.value}</div></div>)}</div>;
}

function RecordDetail({ module, row, detail, onBack, onSaved }: { module: AdminModule; row: AdminRow; detail: DetailRecord; onBack: () => void; onSaved: () => void }) {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [noticeTitle, setNoticeTitle] = useState(textValue(detail, "title"));
  const [noticeMethod, setNoticeMethod] = useState(textValue(detail, "publishMethod") || "immediate");
  const [noticeStart, setNoticeStart] = useState(textValue(detail, "publishStartAt").slice(0, 10) || (row.id === "new" ? todayInKorea() : ""));
  const [noticeEnd, setNoticeEnd] = useState(textValue(detail, "publishEndAt").slice(0, 10));
  const [noticeBody, setNoticeBody] = useState(textValue(detail, "bodyHtml"));
  const [noticeStatus, setNoticeStatus] = useState(textValue(detail, "status") || "active");
  const [answer, setAnswer] = useState(textValue(detail, "answerBody"));
  const saveRecord = async () => {
    if (module === "notices") {
      await saveNotice({ title: noticeTitle, publishMethod: noticeMethod, publishStartAt: noticeStart, publishEndAt: noticeEnd || null, bodyHtml: noticeBody, status: noticeStatus }, row.id === "new" ? undefined : row.id);
    } else if (module === "inquiries") {
      await answerInquiry(row.id, answer);
    }
    onSaved();
  };
  if (module === "orders") return <section className="bo-detail-page">
    <DetailSection title="주문자 정보"><InfoGrid entries={[{ label: "회원명", value: textValue(detail, "user_name") || row.member }, { label: "회원 연락처", value: textValue(detail, "user_phone") || row.phone }, { label: "회원 이메일", value: textValue(detail, "user_email"), span: 2 }]} /></DetailSection>
    <DetailSection title="상품 정보"><InfoGrid entries={[{ label: "상품명", value: textValue(detail, "product_name_snapshot") || row.product, span: 3 }, { label: "수량", value: textValue(detail, "quantity") }, { label: "상품금액", value: moneyValue(detail.unit_price) }, { label: "결제금액", value: moneyValue(detail.total_amount) }]} /></DetailSection>
    <DetailSection title="주문/배송 정보"><InfoGrid entries={[{ label: "주문 번호", value: textValue(detail, "order_number") || row.orderNo, span: 2 }, { label: "주문상태", value: row.status }, { label: "주문 일시", value: dateValue(detail.ordered_at) }, { label: "수령자명", value: textValue(detail, "recipient_name"), span: 2 }, { label: "수령자 연락처", value: textValue(detail, "recipient_phone") }, { label: "배송 주소", value: [detail.postal_code, detail.address_line1, detail.address_line2].filter(Boolean).join(" "), span: 3 }, { label: "배송 요청사항", value: textValue(detail, "delivery_request") || "-", span: 3 }, { label: "택배사", value: textValue(detail, "carrier_name") || "-" }, { label: "송장번호", value: textValue(detail, "tracking_number") || "-", span: 2 }]} /></DetailSection>
    <DetailSection title="주문기록 정보"><InfoGrid columns={2} entries={[{ label: "최초 주문일시", value: dateValue(detail.ordered_at) }, { label: "주문확인 일시", value: dateValue(detail.confirmed_at) }, { label: "배송출발 일시", value: dateValue(detail.shipped_at) }, { label: "배송완료 일시", value: dateValue(detail.delivered_at) }, { label: "주문취소 일시", value: dateValue(detail.cancelled_at), span: 2 }]} /></DetailSection>
    <DetailSection title="결제 정보"><InfoGrid entries={[{ label: "결제 키", value: textValue(detail, "payment_key") || "-", span: 2 }, { label: "결제 상태", value: paymentStatusLabel[textValue(detail, "payment_status")] || textValue(detail, "payment_status") || "-" }, { label: "결제 수단", value: textValue(detail, "payment_method") || "-" }, { label: "결제 금액", value: moneyValue(detail.total_amount), span: 2 }]} /></DetailSection>
    <div className="bo-form-actions"><button className="bo-button outline" onClick={onBack}>목록</button></div>
  </section>;
  if (module === "quotes") return <section className="bo-detail-page">
    <DetailSection title="회원 정보"><InfoGrid entries={[{ label: "회원명", value: textValue(detail, "user_name") || row.member }, { label: "회원 연락처", value: textValue(detail, "user_phone") || row.phone }, { label: "회원 이메일", value: textValue(detail, "user_email") }]} /></DetailSection>
    <DetailSection title="추천구성 정보"><InfoGrid entries={[{ label: "상품명", value: textValue(detail, "product_name"), span: 3 }, { label: "용도", value: textValue(detail, "purpose") }, { label: "분위기", value: textValue(detail, "mood") || "-" }, { label: "예산", value: detail.budget_min || detail.budget_max ? `${Number(detail.budget_min || 0).toLocaleString()}원 ~ ${detail.budget_max ? `${Number(detail.budget_max).toLocaleString()}원` : "이상"}` : "-" }, { label: "수량", value: detail.quantity ? `${detail.quantity}개` : "-" }, { label: "등록 일시", value: dateValue(detail.created_at), span: 2 }, { label: "추가 요청사항", value: textValue(detail, "additional_request") || "-", span: 3 }]} /></DetailSection>
    <DetailSection title="추천결과 정보"><InfoGrid entries={[{ label: "제품명", value: textValue(detail, "recommendation_name") || "-", span: 3 }, { label: "패키징방향", value: textValue(detail, "recommendation_direction") || "-", span: 3 }, { label: "추천 구성", value: textValue(detail, "recommendation_composition") || "-", span: 3 }, { label: "패키징 포인트", value: textValue(detail, "recommendation_points") || "-", span: 3 }]} /></DetailSection>
    <DetailSection title="견적 요청정보"><InfoGrid columns={2} entries={[{ label: "희망납기일", value: textValue(detail, "desired_delivery_date") || row.due }, { label: "추가 요청사항", value: textValue(detail, "additional_request") || "-" }]} /></DetailSection>
    <QuoteHistory quoteId={row.id} offers={Array.isArray(detail.offers) ? detail.offers as DetailRecord[] : []} hideActions={row.status === "상담중"} onRegister={() => setQuoteOpen(true)} onDeleted={onSaved} />
    {row.status === "상담중" ? <ConsultationMemo quoteId={row.id} memos={Array.isArray(detail.memos) ? detail.memos as DetailRecord[] : []} onSaved={onSaved} /> : null}
    <div className="bo-form-actions"><button className="bo-button outline" onClick={onBack}>목록</button></div>
    {quoteOpen ? <QuoteRegistrationModal onClose={() => setQuoteOpen(false)} onConfirm={async (payload) => { await createQuoteOffer(row.id, payload); setQuoteOpen(false); onSaved(); }} /> : null}
  </section>;
  if (module === "notices") return <section className="bo-detail-page">
    <DetailSection title="공지 등록 정보"><FieldRow label="공지번호" value={textValue(detail, "managementCode") || row.noticeNo} /><FieldRow label="게시 방식"><label><input type="radio" name="notice-method" checked={noticeMethod === "immediate"} onChange={() => { setNoticeMethod("immediate"); setNoticeStart(todayInKorea()); }} /> 즉시</label><label><input type="radio" name="notice-method" checked={noticeMethod === "scheduled"} onChange={() => setNoticeMethod("scheduled")} /> 지정</label></FieldRow><FieldRow label="제목" required><input value={noticeTitle} onChange={(event) => setNoticeTitle(event.target.value)} placeholder="공지 제목을 입력해 주세요." /></FieldRow><FieldRow label="게시 기간" required><input type="date" min={todayInKorea()} disabled={noticeMethod === "immediate"} value={noticeStart} onChange={(event) => setNoticeStart(event.target.value)} /><span>~</span><input type="date" min={noticeStart || todayInKorea()} value={noticeEnd} onChange={(event) => setNoticeEnd(event.target.value)} /></FieldRow><FieldRow label="공지 내용"><textarea value={noticeBody} onChange={(event) => setNoticeBody(event.target.value)} /></FieldRow><FieldRow label="게시 상태" required><label><input type="radio" name="notice-status" checked={noticeStatus === "active"} onChange={() => setNoticeStatus("active")} /> 사용</label><label><input type="radio" name="notice-status" checked={noticeStatus === "inactive"} onChange={() => setNoticeStatus("inactive")} /> 미사용</label></FieldRow></DetailSection>
    <div className="bo-form-actions"><button className="bo-button outline" onClick={onBack}>목록</button><button className="bo-button primary" disabled={!noticeTitle.trim() || !noticeStart || !noticeEnd || !noticeStatus} onClick={() => void saveRecord()}>저장</button></div>
  </section>;
  return <section className="bo-detail-page">
    <DetailSection title="문의 상세·답변"><InfoGrid entries={[{ label: "문의번호", value: textValue(detail, "managementCode") || row.inquiryNo, span: 3 }, { label: "문의자", value: textValue(detail, "userName") || row.member }, { label: "휴대폰 번호", value: textValue(detail, "phone") || row.phone }, { label: "이메일", value: textValue(detail, "email") }, { label: "제목", value: textValue(detail, "title"), span: 3 }, { label: "문의유형", value: textValue(detail, "inquiryType") || row.type, span: 3 }, { label: "접수일", value: dateValue(detail.createdAt), span: 3 }, { label: "내용", value: textValue(detail, "body"), span: 3 }, { label: "관리자 답변", value: <input className="bo-answer-input" value={answer} onChange={(event) => setAnswer(event.target.value)} placeholder="답변 내용을 입력해 주세요." />, span: 3 }]} /></DetailSection>
    <div className="bo-form-actions"><button className="bo-button outline" onClick={onBack}>목록</button><button className="bo-button primary" disabled={!answer.trim()} onClick={() => void saveRecord()}>저장</button></div>
  </section>;
}

function InlineModal({ title, children, onClose, onConfirm }: React.PropsWithChildren<{ title: string; onClose: () => void; onConfirm: () => void }>) {
  return <div className="bo-dialog-layer"><section className="bo-dialog bo-form-dialog"><button className="bo-dialog-close" type="button" onClick={onClose} aria-label="닫기"><X size={20} /></button><h2>{title}</h2><div className="bo-dialog-fields">{children}</div><div><button className="bo-button outline" onClick={onClose}>취소</button><button className="bo-button primary" onClick={onConfirm}>저장</button></div></section></div>;
}

function OrderStatusModal({ action, rows, onClose, onConfirm }: { action: string; rows: AdminRow[]; onClose: () => void; onConfirm: () => void | Promise<void> }) {
  return <div className="bo-dialog-layer"><section className="bo-dialog bo-wide-dialog">
    <h2>{action} 다중 처리 팝업</h2>
    <div className="bo-modal-table-wrap"><table className="bo-data-table"><thead><tr><th>No</th><th>주문 번호</th><th>상품명</th><th>주문회원</th><th>연락처</th><th>주소</th><th>결제 금액</th><th>결제 수단</th></tr></thead><tbody>{rows.map((row) => <tr key={row.id}><td>{row.no}</td><td>{row.orderNo}</td><td>{row.product}</td><td>{row.member}</td><td>{row.phone}</td><td title={row.address}><span>{row.address}</span></td><td>{row.amount}</td><td>{row.method}</td></tr>)}</tbody></table></div>
    <div className="bo-modal-actions"><button className="bo-button muted" type="button" onClick={onClose}>취소</button><button className="bo-button primary" type="button" onClick={() => void onConfirm()}>승인</button></div>
  </section></div>;
}

function ShipmentModal({ rows, onClose, onConfirm }: { rows: AdminRow[]; onClose: () => void; onConfirm: (shipments: Array<{ id: string; carrierCode: string; carrierName: string; trackingNumber: string }>) => void | Promise<void> }) {
  const [shipments, setShipments] = useState<Record<string, { carrierCode: string; carrierName: string; trackingNumber: string }>>(() => Object.fromEntries(rows.map((row) => [row.id, { carrierCode: "", carrierName: "", trackingNumber: "" }])));
  const updateShipment = (id: string, key: "carrierName" | "trackingNumber", value: string) => setShipments((current) => ({ ...current, [id]: { ...current[id], [key]: value, ...(key === "carrierName" ? { carrierCode: value } : {}) } }));
  const canSave = rows.every((row) => shipments[row.id]?.carrierName && shipments[row.id]?.trackingNumber);
  return <div className="bo-dialog-layer"><section className="bo-dialog bo-wide-dialog">
    <h2>송장 입력 (배송 처리)</h2><p className="bo-dialog-question">{rows.length}개의 주문건을 출고 처리 하시겠습니까?</p>
    <div className="bo-modal-table-wrap"><table className="bo-data-table bo-shipment-table"><thead><tr><th>No</th><th>주문 번호</th><th>상품명</th><th>주소</th><th>주문회원</th><th>택배사</th><th>송장 번호</th></tr></thead><tbody>{rows.map((row) => <tr key={row.id}><td>{row.no}</td><td>{row.orderNo}</td><td>{row.product}</td><td title={row.address}><span>{row.address}</span></td><td>{row.member}</td><td><select aria-label={`${row.orderNo} 택배사`} value={shipments[row.id]?.carrierName || ""} onChange={(event) => updateShipment(row.id, "carrierName", event.target.value)}><option value="">선택</option><option>우체국택배</option><option>CJ대한통운</option><option>한진택배</option><option>롯데택배</option></select></td><td><input aria-label={`${row.orderNo} 송장번호`} value={shipments[row.id]?.trackingNumber || ""} onChange={(event) => updateShipment(row.id, "trackingNumber", event.target.value)} placeholder="ex. 230940923" /></td></tr>)}</tbody></table></div>
    <div className="bo-modal-actions"><button className="bo-button muted" type="button" onClick={onClose}>취소</button><button className="bo-button primary" disabled={!canSave} type="button" onClick={() => void onConfirm(rows.map((row) => ({ id: row.id, ...shipments[row.id] })))}>저장</button></div>
  </section></div>;
}

function QuoteRegistrationModal({ onClose, onConfirm }: { onClose: () => void; onConfirm: (payload: DetailRecord) => Promise<void> }) {
  const [costs, setCosts] = useState({ manufacture: "", package: "", sticker: "", design: "", delivery: "", other: "" });
  const [productionDays, setProductionDays] = useState("");
  const [quantity, setQuantity] = useState("");
  const [packagingComposition, setPackagingComposition] = useState("");
  const [productComposition, setProductComposition] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const totalCost = Object.values(costs).reduce((sum, value) => sum + (Number(value) || 0), 0);
  const costInput = (key: keyof typeof costs, placeholder = "가격을 입력해 주세요.") => <input type="number" min="0" value={costs[key]} onChange={(event) => setCosts((current) => ({ ...current, [key]: event.target.value }))} placeholder={placeholder} />;
  return <div className="bo-dialog-layer"><section className="bo-dialog bo-quote-dialog">
    <h2>견적 등록</h2>
    <div className="bo-quote-form-grid">
      <label><span>총 견적가</span><div><input value={totalCost.toLocaleString()} readOnly /><em>원</em></div></label>
      <label className="is-required"><span>제작 기간</span><div><input type="number" min="1" value={productionDays} onChange={(event) => setProductionDays(event.target.value)} placeholder="제작기간을 입력해 주세요." /><em>일</em></div></label>
      <label className="is-required"><span>제조원가</span><div>{costInput("manufacture")}<em>원</em></div></label>
      <label className="is-required"><span>포장비</span><div>{costInput("package")}<em>원</em></div></label>
      <label className="is-required"><span>스티커</span><div>{costInput("sticker")}<em>원</em></div></label>
      <label className="is-required"><span>디자인비</span><div>{costInput("design")}<em>원</em></div></label>
      <label className="is-required"><span>배송비</span><div>{costInput("delivery")}<em>원</em></div></label>
      <label className="is-required"><span>기타금액</span><div>{costInput("other")}<em>원</em></div></label>
      <label className="span-two is-required"><span>수량</span><div><input type="number" min="1" value={quantity} onChange={(event) => setQuantity(event.target.value)} placeholder="수량을 입력해 주세요." /><em>개</em></div></label>
      <label className="span-two"><span>포장구성</span><div><input value={packagingComposition} onChange={(event) => setPackagingComposition(event.target.value)} placeholder="포장구성을 입력해 주세요." /></div></label>
      <label className="span-two"><span>상품구성</span><div><input value={productComposition} onChange={(event) => setProductComposition(event.target.value)} placeholder="상품구성을 입력해 주세요." /></div></label>
      <label className="span-two"><span>기타안내</span><div><input placeholder="기타안내를 입력해 주세요." /><em>개</em></div></label>
      <div className="span-two bo-quote-file"><span>이미지</span><div><input id="quote-image" type="file" accept="image/jpeg,image/png" onChange={(event) => setImage(event.target.files?.[0] || null)} /><label htmlFor="quote-image">업로드</label><button type="button" disabled={!image}>{image?.name || "선택된 파일 없음"}</button></div></div>
    </div>
    <div className="bo-modal-actions"><button className="bo-button muted" type="button" onClick={onClose}>취소</button><button className="bo-button primary" disabled={saving || !totalCost || !Number(productionDays) || !Number(quantity)} type="button" onClick={async () => { setSaving(true); try { const uploaded = image ? await uploadFile(image, "quote-offer") : null; await onConfirm({ totalAmount: totalCost, productionDays: Number(productionDays), quantity: Number(quantity), manufactureCost: Number(costs.manufacture) || 0, packagingCost: Number(costs.package) || 0, stickerCost: Number(costs.sticker) || 0, designCost: Number(costs.design) || 0, deliveryCost: Number(costs.delivery) || 0, otherCost: Number(costs.other) || 0, packagingComposition, productComposition, imageFileId: uploaded?.id }); } finally { setSaving(false); } }}>{saving ? "등록 중" : "견적등록"}</button></div>
  </section></div>;
}

function QuoteHistory({ quoteId, offers, hideActions = false, onRegister, onDeleted }: { quoteId: string; offers: DetailRecord[]; hideActions?: boolean; onRegister: () => void; onDeleted: () => void }) {
  const [selected, setSelected] = useState<string[]>([]);
  return <section className="bo-form-section bo-quote-history"><div className="bo-section-title"><h2>견적정보</h2>{!hideActions && <div>{offers.length ? <button className="bo-button outline small" type="button" disabled={!selected.length} onClick={() => void Promise.all(selected.map((id) => deleteQuoteOffer(quoteId, id))).then(onDeleted)}>선택 삭제</button> : null}<button className="bo-button primary small" type="button" onClick={onRegister}>견적 등록</button></div>}</div><div className="bo-table-wrap"><table className="bo-data-table"><thead><tr>{!hideActions && <th className="bo-check-cell"><input type="checkbox" aria-label="견적 전체 선택" checked={offers.length > 0 && selected.length === offers.length} onChange={() => setSelected(selected.length === offers.length ? [] : offers.map((item) => String(item.id)))} /></th>}<th>No</th><th>총 견적가(원)</th><th>제작기간(일)</th><th>수량(개)</th><th>제조원가(원)</th><th>디자인비(원)</th><th>포장비(원)</th><th>등록일시</th></tr></thead><tbody>{offers.length ? offers.map((item, index) => <tr key={String(item.id)}>{!hideActions && <td className="bo-check-cell"><input type="checkbox" aria-label={`${index + 1}번 견적 선택`} checked={selected.includes(String(item.id))} onChange={() => setSelected((current) => current.includes(String(item.id)) ? current.filter((id) => id !== String(item.id)) : [...current, String(item.id)])} /></td>}<td>{String(offers.length - index).padStart(2, "0")}</td><td>{moneyValue(item.total_amount)}</td><td>{String(item.production_days || 0)}일</td><td>{String(item.quantity || 0)}개</td><td>{moneyValue(item.manufacture_cost)}</td><td>{moneyValue(item.design_cost)}</td><td>{moneyValue(item.packaging_cost)}</td><td>{dateValue(item.created_at)}</td></tr>) : <tr><td className="bo-empty-cell" colSpan={hideActions ? 8 : 9}>등록된 견적이 없습니다.</td></tr>}</tbody></table></div></section>;
}

function ConsultationMemo({ quoteId, memos, onSaved }: { quoteId: string; memos: DetailRecord[]; onSaved: () => void }) {
  const [memo, setMemo] = useState("");
  const saveMemo = async () => {
    if (!memo.trim()) return;
    await addQuoteMemo(quoteId, memo.trim());
    setMemo("");
    onSaved();
  };
  return <section className="bo-form-section bo-consultation"><h2>상담 메모</h2><div className="bo-consultation-entry"><span>메모</span><input value={memo} onChange={(event) => setMemo(event.target.value)} placeholder="메모를 입력해 주세요." /><button className="bo-button primary tiny" type="button" disabled={!memo.trim()} onClick={() => void saveMemo()}>메모 등록</button></div><p>*메모는 4개까지 작성 가능하며, 4개 초과 시 과거순으로 메모가 삭제됩니다.</p>{memos.length ? memos.map((item) => <div className="bo-consultation-item" key={String(item.id)}><span>- [{dateValue(item.createdAt)}] {String(item.body || "")}</span><button type="button" aria-label="상담 메모 삭제" onClick={() => void deleteQuoteMemo(quoteId, String(item.id)).then(onSaved)}><X size={18} /></button></div>) : <div className="bo-consultation-item"><span>- 등록된 메모가 없습니다.</span></div>}</section>;
}

function AdminApp() {
  const [authenticated, setAuthenticated] = useState(() => Boolean(getApiToken()));
  const [activeModule, setActiveModule] = useState<AdminModule>(moduleFromUrl);
  const [detailId, setDetailId] = useState(() => new URL(window.location.href).searchParams.get("detail") || "");
  const [selectedId, setSelectedId] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchValues, setSearchValues] = useState<Record<string, string>>({});
  const [dialog, setDialog] = useState<DialogState>(null);
  const [orderAction, setOrderAction] = useState<{ kind: "status" | "shipment"; action: string } | null>(null);
  const [apiRows, setApiRows] = useState<AdminRow[]>([]);
  const [pagination, setPagination] = useState<ApiPagination>({ page: 1, pageSize: 10, total: 0, totalPages: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [detailData, setDetailData] = useState<DetailRecord | null>(null);
  const [memberDetailData, setMemberDetailData] = useState<DetailRecord | null>(null);

  useEffect(() => {
    const onPop = () => {
      setActiveModule(moduleFromUrl());
      setDetailId(new URL(window.location.href).searchParams.get("detail") || "");
      setSelectedId("");
      setSelectedIds([]);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    let active = true;
    setLoading(true);
    setLoadError("");
    fetchModuleRows(activeModule, searchValues, page)
      .then((result) => {
        if (!active) return;
        setApiRows(result.rows);
        setPagination(result.pagination);
      })
      .catch((error) => {
        if (!active) return;
        setApiRows([]);
        setLoadError(error instanceof Error ? error.message : "데이터를 불러오지 못했습니다.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [authenticated, activeModule, searchValues, page, reloadKey]);

  const refreshDetail = async () => {
    if (!detailId || detailId === "new") return;
    setDetailData(await fetchModuleDetail(activeModule, detailId));
  };

  const refreshMemberDetail = async () => {
    if (!selectedId || activeModule !== "members") return;
    setMemberDetailData(await fetchModuleDetail("members", selectedId));
  };

  useEffect(() => {
    setDetailData(null);
    if (!authenticated || !detailId || detailId === "new") return;
    void refreshDetail().catch((error) => setLoadError(error instanceof Error ? error.message : "상세 정보를 불러오지 못했습니다."));
  }, [authenticated, activeModule, detailId, reloadKey]);

  useEffect(() => {
    setMemberDetailData(null);
    if (!authenticated || activeModule !== "members" || !selectedId) return;
    void refreshMemberDetail().catch((error) => setLoadError(error instanceof Error ? error.message : "회원 상세 정보를 불러오지 못했습니다."));
  }, [authenticated, activeModule, selectedId, reloadKey]);

  const config = adminModules.find((module) => module.id === activeModule)!;
  const rows = apiRows;
  const selectedRow = rows.find((row) => row.id === (detailId || selectedId));
  const fetchedDetailRow = detailId && detailData ? rowFromDetail(activeModule, detailId, detailData) : undefined;
  const detailRow = detailId === "new" && activeModule === "notices"
    ? { id: "new", noticeNo: "저장 시 자동 생성", title: "", status: "게시중" }
    : selectedRow || fetchedDetailRow;

  if (!authenticated) return <Login onLogin={() => setAuthenticated(true)} />;

  const openModule = (module: AdminModule) => {
    setActiveModule(module);
    setSelectedId("");
    setSelectedIds([]);
    setSearchValues({});
    setPage(1);
    updateUrl(module);
  };
  const openDetail = (id: string) => updateUrl(activeModule, id);
  const closeDetail = () => updateUrl(activeModule);
  const saved = (preserveScroll = false) => {
    const scrollTop = window.scrollY;
    setReloadKey((value) => value + 1);
    setDialog({ title: "저장완료", message: "입력한 정보가 저장되었습니다." });
    if (preserveScroll) [0, 80, 240].forEach((delay) => window.setTimeout(() => window.scrollTo({ top: scrollTop }), delay));
  };
  const toggleSelected = (id: string) => setSelectedIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  const selectedOrderRows = activeModule === "orders" ? rows.filter((row) => selectedIds.includes(row.id)) : [];
  const allowedOrderStatus: Record<string, string[]> = {
    "주문 취소": ["주문완료", "주문확인", "배송중", "배송완료"],
    "주문확인": ["주문완료"],
    "출고": ["주문확인"],
    "배송완료": ["배송중"],
    "확정": ["배송완료"],
  };
  const openOrderAction = (action: string, kind: "status" | "shipment" = "status") => {
    if (!selectedOrderRows.length) {
      setDialog({ title: "주문 선택", message: "처리할 주문을 선택해 주세요." });
      return;
    }
    const invalidRows = selectedOrderRows.filter((row) => !allowedOrderStatus[action]?.includes(row.status));
    if (invalidRows.length) {
      setDialog({ title: "상태 변경 불가", message: `${action} 처리가 가능한 상태의 주문만 선택해 주세요. 주문 상태는 이전 단계로 되돌릴 수 없습니다.` });
      return;
    }
    setOrderAction({ action, kind });
  };
  const selectable = ["products", "orders", "quotes", "notices"].includes(activeModule);
  const handleRowOpen = (id: string) => openDetail(id);
  const runBulkAction = (title: string, message: string) => {
    if (!selectedIds.length) {
      setDialog({ title: "선택 확인", message: "처리할 항목을 먼저 선택해 주세요." });
      return;
    }
    setDialog({ title, message });
  };
  const removeSelectedProducts = async () => {
    await Promise.all(selectedIds.map(deleteProduct));
    setSelectedIds([]);
    setReloadKey((value) => value + 1);
    setDialog({ title: "삭제완료", message: "선택한 상품이 삭제되었습니다." });
  };
  const removeSelectedNotices = async () => {
    await Promise.all(selectedIds.map(deleteNotice));
    setSelectedIds([]);
    setReloadKey((value) => value + 1);
    setDialog({ title: "삭제완료", message: "선택한 공지사항이 삭제되었습니다." });
  };
  const removeSelectedQuotes = async () => {
    await Promise.all(selectedIds.map(deleteQuote));
    setSelectedIds([]);
    setReloadKey((value) => value + 1);
    setDialog({ title: "삭제완료", message: "선택한 견적요청이 삭제되었습니다." });
  };
  const completeSelectedQuotes = async () => {
    await Promise.all(selectedIds.map((id) => updateQuote(id, { status: "paid" })));
    setSelectedIds([]);
    setReloadKey((value) => value + 1);
    setDialog({ title: "처리완료", message: "선택한 견적이 결제완료 처리되었습니다." });
  };
  const orderStatusByAction: Record<string, string> = { "주문 취소": "cancelled", "주문확인": "confirmed", "배송완료": "delivered", "확정": "completed" };
  const confirmOrderStatus = async () => {
    if (!orderAction) return;
    await Promise.all(selectedOrderRows.map((row) => updateOrder(row.id, { status: orderStatusByAction[orderAction.action] })));
    const action = orderAction.action;
    setOrderAction(null);
    setSelectedIds([]);
    setReloadKey((value) => value + 1);
    setDialog({ title: "처리완료", message: `선택한 주문이 ${action} 처리되었습니다.` });
  };
  const confirmShipments = async (shipments: Array<{ id: string; carrierCode: string; carrierName: string; trackingNumber: string }>) => {
    await Promise.all(shipments.map(({ id, ...shipment }) => updateOrder(id, { status: "shipping", ...shipment })));
    setOrderAction(null);
    setSelectedIds([]);
    setReloadKey((value) => value + 1);
    setDialog({ title: "저장완료", message: "송장정보가 저장되고 출고 처리되었습니다." });
  };

  return (
    <main className="bo-shell">
      <Sidebar active={activeModule} onSelect={openModule} onLogout={() => setDialog({
        title: "로그아웃",
        message: "관리자 페이지에서 로그아웃하시겠습니까?",
        cancelLabel: "취소",
        confirmLabel: "로그아웃",
        onConfirm: () => { requestApi("/admin/auth/logout", { method: "POST" }).catch(() => undefined); setApiToken(""); setAuthenticated(false); },
      })} />
      <section className="bo-workspace">
        <header className="bo-topbar"><h1>{detailId ? activeModule === "products" ? "상품 상세 정보" : activeModule === "orders" ? "주문 상세" : activeModule === "quotes" ? "견적요청 상세" : `${config.title.replace(" 관리", "")} 상세` : config.title}</h1></header>
        <div className="bo-content">
          {detailId && detailRow && activeModule === "members" && detailData ? <MemberDetail row={detailRow} detail={detailData} onRefresh={refreshDetail} onSaved={saved} onPasswordReset={() => { void resetMemberPassword(detailRow.id).then(({ password }) => setDialog({ title: "비밀번호 초기화 완료", message: `초기화된 비밀번호는 ${password}입니다.` })).catch((error) => setDialog({ title: "초기화 실패", message: error instanceof Error ? error.message : "비밀번호를 초기화하지 못했습니다." })); }} onOpenOrder={(id) => { setActiveModule("orders"); updateUrl("orders", id); }} /> : null}
          {detailId && detailRow && activeModule === "products" && detailData ? <ProductDetail row={detailRow} detail={detailData} onBack={closeDetail} onSaved={saved} onDeleted={() => setDialog({ title: "상품 삭제", message: "이 상품을 삭제하시겠습니까?", cancelLabel: "취소", confirmLabel: "삭제", onConfirm: () => { void deleteProduct(detailRow.id).then(() => { closeDetail(); saved(); }); } })} /> : null}
          {detailId && detailRow && ["orders", "quotes", "notices", "inquiries"].includes(activeModule) && (detailData || detailId === "new") ? <RecordDetail module={activeModule} row={detailRow} detail={detailData || {}} onBack={closeDetail} onSaved={detailId === "new" ? () => { closeDetail(); saved(); } : saved} /> : null}
          {!detailId ? <>
            <SearchPanel config={config} onSearch={(values) => { setPage(1); setSearchValues(values); }} />
            <section className="bo-list-section">
              <div className="bo-section-title"><h2>{config.listTitle}</h2><div className="bo-list-actions">
                {activeModule === "products" ? <><button className="bo-button outline small" type="button" onClick={() => { if (!selectedIds.length) { setDialog({ title: "선택 확인", message: "삭제할 상품을 먼저 선택해 주세요." }); return; } setDialog({ title: "상품 삭제", message: `선택한 ${selectedIds.length}개 상품을 삭제하시겠습니까?`, cancelLabel: "취소", confirmLabel: "삭제", onConfirm: () => { void removeSelectedProducts(); } }); }}>선택 삭제</button><button className="bo-button primary small" type="button" onClick={() => updateUrl(activeModule, "new")}><Plus size={16} />상품 등록</button></> : null}
                {activeModule === "orders" ? <><button className="bo-button danger small" type="button" onClick={() => openOrderAction("주문 취소")}>주문 취소</button><button className="bo-button blue-outline small" type="button" onClick={() => openOrderAction("주문확인")}>주문확인</button><button className="bo-button blue-outline small" type="button" onClick={() => openOrderAction("출고", "shipment")}>출고</button><button className="bo-button blue-outline small" type="button" onClick={() => openOrderAction("배송완료")}>배송완료</button><button className="bo-button primary small" type="button" onClick={() => openOrderAction("확정")}>확정</button></> : null}
                {activeModule === "quotes" ? <><button className="bo-button outline small" type="button" onClick={() => { if (!selectedIds.length) { setDialog({ title: "선택 확인", message: "삭제할 견적요청을 먼저 선택해 주세요." }); return; } setDialog({ title: "견적요청 삭제", message: `선택한 ${selectedIds.length}개 견적요청을 삭제하시겠습니까?`, cancelLabel: "취소", confirmLabel: "삭제", onConfirm: () => { void removeSelectedQuotes(); } }); }}>선택 삭제</button><button className="bo-button primary small" type="button" onClick={() => { if (!selectedIds.length) { setDialog({ title: "선택 확인", message: "결제완료 처리할 견적을 먼저 선택해 주세요." }); return; } void completeSelectedQuotes(); }}>결제</button></> : null}
                {activeModule === "notices" ? <><button className="bo-button outline small" type="button" onClick={() => { if (!selectedIds.length) { setDialog({ title: "선택 확인", message: "삭제할 공지사항을 먼저 선택해 주세요." }); return; } setDialog({ title: "공지사항 삭제", message: `선택한 ${selectedIds.length}개 공지사항을 삭제하시겠습니까?`, cancelLabel: "취소", confirmLabel: "삭제", onConfirm: () => { void removeSelectedNotices(); } }); }}>선택 삭제</button><button className="bo-button primary small" type="button" onClick={() => updateUrl(activeModule, "new")}><Plus size={16} />신규 등록</button></> : null}
              </div></div>
              {loading ? <p className="bo-list-state">데이터를 불러오는 중입니다.</p> : loadError ? <p className="bo-list-state is-error">{loadError}</p> : rows.length ? <DataTable config={config} rows={rows} selectedId={selectedId} selectedIds={selectedIds} selectable={selectable} onSelect={setSelectedId} onOpen={handleRowOpen} onToggle={toggleSelected} /> : <p className="bo-list-state">조회된 데이터가 없습니다.</p>}
              <Pagination value={pagination} onChange={setPage} />
            </section>
            {activeModule === "members" && selectedRow && memberDetailData ? <MemberDetail row={selectedRow} detail={memberDetailData} onRefresh={refreshMemberDetail} onSaved={saved} onPasswordReset={() => { void resetMemberPassword(selectedRow.id).then(({ password }) => setDialog({ title: "비밀번호 초기화 완료", message: `초기화된 비밀번호는 ${password}입니다.` })).catch((error) => setDialog({ title: "초기화 실패", message: error instanceof Error ? error.message : "비밀번호를 초기화하지 못했습니다." })); }} onOpenOrder={(id) => { setActiveModule("orders"); updateUrl("orders", id); }} /> : null}
          </> : null}
          {detailId === "new" && activeModule === "products" ? <ProductDetail onBack={closeDetail} onSaved={() => { closeDetail(); saved(); }} onDeleted={saved} /> : null}
        </div>
      </section>
      {orderAction?.kind === "status" ? <OrderStatusModal action={orderAction.action} rows={selectedOrderRows} onClose={() => setOrderAction(null)} onConfirm={confirmOrderStatus} /> : null}
      {orderAction?.kind === "shipment" ? <ShipmentModal rows={selectedOrderRows} onClose={() => setOrderAction(null)} onConfirm={confirmShipments} /> : null}
      <AppDialog dialog={dialog} onClose={() => setDialog(null)} />
    </main>
  );
}

createRoot(document.getElementById("root")!).render(<StrictMode><AdminApp /></StrictMode>);
